import { useState } from "react";
import { useMutationData } from "./useMutationData";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useTranslation } from "react-i18next";
import { isNotNullAndEmptyArray } from "@utils/common";

export const useAssetClassification = () => {
  const { t } = useTranslation();
  const [assetClassificationList, setAssetClassificationList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get asset classification list
  const getAssetClassificationList = useMutationData(
    "asset_classifications",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const sortedList = [...(response || [])].sort(
            (a, b) => a.assetClassificationSeq - b.assetClassificationSeq,
          );
          setAssetClassificationList(sortedList);
        }
      },
      onError: (error) => {
        console.error("Error during get asset classification list:", error);
        showErrorToast(t("policy.get-asset-classification-list-error"));
      },
    },
  );

  // Create asset classification
  const createAssetClassification = useMutationData(
    "asset_classification",
    { method: "POST" },
    {
      onSuccess: () => {
        showSuccessToast(t("policy.asset-classification-created-successfully"));
      },
      onError: (error) => {
        console.error("Error creating asset classification:", error);
        const errorMessage = error?.response?.data?.message || error?.response?.data || "";
        if (errorMessage.includes("Classification Name is existed.")) {
          showErrorToast(t("common.error"), t("policy.asset-classification-name-existed"));
        } else {
          showErrorToast(t("policy.asset-classification-created-error"));
        }
      },
    },
  );

  // Update asset classification
  const updateAssetClassification = useMutationData(
    (params) => {
      const { assetClassificationSeq } = params;
      return `asset_classification/${assetClassificationSeq}`;
    },
    { method: "PUT" },
    {
      onSuccess: () => {
        showSuccessToast(t("policy.asset-classification-updated-successfully"));
      },
      onError: (error) => {
        console.error("Error updating asset classification:", error);
        const errorMessage = error?.response?.data?.message || error?.response?.data || "";
        if (errorMessage.includes("Classification Name is existed.")) {
          showErrorToast(t("common.error"), t("policy.asset-classification-name-existed"));
        } else {
          showErrorToast(t("policy.asset-classification-updated-error"));
        }
      },
    },
  );

  // Delete asset classification
  const deleteAssetClassification = useMutationData(
    "asset_classifications",
    { method: "DELETE" },
    {
      onSuccess: () => {
        showSuccessToast(t("policy.asset-classification-deleted-successfully"));
      },
      onError: (error) => {
        if (isNotNullAndEmptyArray(error.response.data?.deletedSeqs)) {
          console.log("error", error.response.data?.deletedSeqs);
          showSuccessToast(
            error.response.data.deletedSeqs.length + t("policy.asset-classification-deleted-successfully"),
          );
          fetchAssetClassificationList();
        }
        console.error("Error deleting asset classification:", error);
        if (error.response.status === 409) {
          showErrorToast(
            t("status.error"),
            error?.response?.data?.message + t("policy.asset-classification-deleted-error-conflict"),
          );
        } else {
          showErrorToast(t("status.error"), t("policy.asset-classification-deleted-error"));
        }
      },
    },
  );

  const fetchAssetClassificationList = (payload = {}) => {
    setIsLoading(true);
    getAssetClassificationList.mutate(payload, {
      onSettled: () => {
        setIsLoading(false);
      }
    });
  };

  return {
    assetClassificationList,
    fetchAssetClassificationList,
    createAssetClassification,
    updateAssetClassification,
    deleteAssetClassification,
    isLoading,
    isCreating: createAssetClassification.isPending,
    isUpdating: updateAssetClassification.isPending,
    isDeleting: deleteAssetClassification.isPending,
  };
}; 